import React, { useState, useEffect } from "react";
import { DialogContainer, Button, TextField, FontIcon } from "react-md";
import { Row, Col } from "react-flexbox-grid";
import successGif from "../../../../../../assets/images/done-animation.gif";
import "../../styles/navigoDashboardStyles.scss";
import { formatCurrency } from "../../helper/formatCurrency";
import EventService from "../../../../../../service/event/EventService";
import NavigoDashboardUtils from "../../services/NavigoDashboardUtils";
import { CircularProgress } from "react-md";
import { REFUND_STATUS } from "../../helper/appointmentConstants";
import { collectedByChannelFromEventHistory } from "../../helper/paymentViewModel";

export function RefundDialog({
  dialogState = {},
  setDialogState,
  handleClose,
}) {
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  // What the server says it would actually refund, per source. Null until the preview lands.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const navigoDashboardUtils = new NavigoDashboardUtils();

  const {
    state,
    width,
    appointmentObj,
    isOnlyRefund,
    forceRefund,
    statusToBeApplied,
    onRefresh,
    newRefundStatus,
    refundResult,
  } = dialogState || {};

  // PARTIAL = some orders refunded, one failed. FAILED = the first order failed, so nothing
  // moved. Both keep the form open for a retry, but they must not claim the same thing:
  // "partially completed" against a totalRefunded of 0 tells the operator money went out when
  // none did.
  const isPartial = refundResult?.status === "PARTIAL";
  const isFailed = refundResult?.status === "FAILED";
  const isRetryable = isPartial || isFailed;

  // Payment summary, in order of preference:
  //   1. the server's refund preview — exactly what confirming will move, per source;
  //   2. the appointment's eventHistory — same accounting, computed client-side;
  //   3. paymentInfo — the legacy scalar, PRIMARY-ORDER-ONLY.
  //
  // (3) is why this section was wrong for cart appointments: they hold several paid ZO-s
  // (checkout + Bridge-approve top-ups) and refunds allocate newest-order-first, so the scalar
  // routinely described neither the money nor the refund. It survives only as a last resort for
  // appointments served without an eventHistory.
  const summary = (() => {
    if (preview) {
      return {
        wallet: preview.wallet,
        external: preview.external,
        total: preview.total,
        source: "preview",
      };
    }
    const events = appointmentObj?.eventHistory;
    if (Array.isArray(events) && events.length > 0) {
      const t = collectedByChannelFromEventHistory(events);
      return { ...t, source: "events" };
    }
    const wallet =
      Number(appointmentObj?.paymentInfo?.walletPayment?.amount) || 0;
    const external =
      Number(appointmentObj?.paymentInfo?.externalPayment?.amount) || 0;
    return {
      wallet,
      external,
      total: Number(appointmentObj?.paymentInfo?.amount) || wallet + external,
      source: "legacy",
    };
  })();

  // Ask the server what it would refund, as soon as the form opens. Preview-only: no side
  // effects, so a failure is non-fatal — we silently fall back to the eventHistory figures.
  useEffect(() => {
    if (state !== 1 || !isOnlyRefund || !appointmentObj?.id) return;
    let cancelled = false;
    setPreviewLoading(true);
    navigoDashboardUtils.previewRefund(
      {
        full: true,
        patientId: appointmentObj?.patientID,
        appointmentId: appointmentObj?.id,
      },
      (resp) => {
        if (cancelled) return;
        setPreviewLoading(false);
        if (resp?.totalRefunded) setPreview(resp.totalRefunded);
      },
      () => {
        if (cancelled) return;
        setPreviewLoading(false);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [state, isOnlyRefund, appointmentObj?.id]);

  const handleCancelAppointment = (value, ele) => {
    setLoading(true);

    const body = {
      appointmentID: ele.id,
      status: value,
      refundRequest: forceRefund ? REFUND_STATUS.FORCED : newRefundStatus,
    };

    const onResponse = (res) => {
      setLoading(false);
      setDialogState((prev) => ({
        ...prev,
        state: 2,
      }));
      onRefresh();
    };

    const onError = (err) => {
      setLoading(false);
      EventService.emit("showError", {
        message: `Error: ${err?.message}`,
      });
      setTyped("");
      setReason("");
      handleClose();
    };

    navigoDashboardUtils.handleStatusUpdate(body, onResponse, onError);
  };

  const getButtonStyle = (color) => {
    const base = {
      cursor: "pointer",
      marginLeft: "10px",
      minWidth: "125px",
      textTransform: "none",
      fontSize: "medium",
      borderRadius: "5px",
      color: "#FFFFFF",
    };

    switch (color) {
      case "zyOrange":
        return { ...base, backgroundColor: "#F77F11" };
      case "zyGreen":
        return { ...base, backgroundColor: "#04C9D3" };
      case "zyDisabled":
        return { ...base, backgroundColor: "#C2C2C2" };
      default:
        return { ...base, backgroundColor: "#EBEBEB", color: "#333333" };
    }
  };

  const handleOnlyRefund = (appointmentObj) => {
    setLoading(true);
    // full=true refunds every remaining rupee across every paid order on the appointment.
    // A cart appointment can hold more than one (checkout + Bridge-approved top-ups), and
    // the server ignores orderId in this mode — it iterates the appointment's orders itself.
    const body = {
      full: true,
      reason: (reason ?? "").toString().trim(),
      patientId: appointmentObj?.patientID,
      appointmentId: appointmentObj?.id,
    };
    const onResponse = (resp) => {
      setLoading(false);
      if (resp?.status === "PARTIAL" || resp?.status === "FAILED") {
        // PARTIAL: some orders refunded, one failed — money already moved is not rolled back.
        // FAILED: the first order failed, so nothing moved at all.
        // Either way stay on the form and let the user retry: the server re-plans against the
        // current ledger, and a failed refund leg is not counted as refunded, so the balance is
        // still fully available.
        setDialogState((prev) => ({ ...prev, refundResult: resp }));
        const failedOn = `Failed on order ${resp?.failedOrderId || "-"}: ${resp?.error || "unknown error"}`;
        EventService.emit("showError", {
          message:
            resp.status === "FAILED"
              ? `Refund failed — no money was refunded. ${failedOn}`
              : `Refund partially completed. ${failedOn}`,
        });
        onRefresh();
        return;
      }
      setDialogState((prev) => ({
        ...prev,
        state: 2,
        refundResult: resp,
      }));
      onRefresh();
    };
    const onError = (err) => {
      setLoading(false);
      // The 409 "nothing left to refund" case. post() never surfaces the HTTP status, but the
      // response body is the same refund envelope, so the status field discriminates it.
      if (err?.status === "NOOP") {
        EventService.emit("showError", {
          message: err?.message || "Nothing left to refund",
        });
        onRefresh();
        setTyped("");
        setReason("");
        handleClose();
        return;
      }
      EventService.emit("showError", { message: `Error: ${err?.message}` });
    };
    navigoDashboardUtils.handleOnlyRefund(body, onResponse, onError);
  };

  // Auto-call API on open if no refund - only cancel appointment
  useEffect(() => {
    if (
      isOnlyRefund === false &&
      state === 1 &&
      newRefundStatus === REFUND_STATUS.ELIGIBLE_NOT_APPLICABLE &&
      appointmentObj &&
      statusToBeApplied
    ) {
      handleCancelAppointment(statusToBeApplied, appointmentObj);
    }
  }, [isOnlyRefund, state, newRefundStatus, appointmentObj, statusToBeApplied]);

  if (!state) return null;

  return (
    <div className="custom-dialog-overlay">
      <DialogContainer
        id="refund-dialog"
        visible={true}
        onHide={() => {}}
        focusOnMount={false}
        autosizeContent
        width={width}
        dialogStyle={{
          borderRadius: "10px",
          padding: "15px",
          position: "relative",
          zIndex: 101,
        }}
      >
        {loading ? (
          <div
            style={{
              height: "401px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
            }}
          >
            <CircularProgress />
            <span>Loading ...</span>
          </div>
        ) : (
          <div style={{ fontFamily: "Montserrat", color: "#000" }}>
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "15px",
              }}
            >
              <div
                style={{
                  margin: 0,
                  textAlign: "center",
                  width: "100%",
                  fontWeight: "600",
                  fontSize: "16px",
                }}
              >
                {state === 1
                  ? forceRefund
                    ? "Refund is NOT applicable"
                    : "Refund details"
                  : ""}
              </div>
              <FontIcon
                style={{
                  cursor: "pointer",
                  fontSize: "22px",
                  color: "#777",
                }}
                onClick={() => {
                  setTyped("");
                  setReason("");
                  handleClose();
                }}
              >
                close
              </FontIcon>
            </div>

            {/* Refund details */}
            {state === 1 && (
              <>
                <div
                  style={{
                    fontSize: "16px",
                    lineHeight: "28px",
                    fontWeight: "500",
                  }}
                >
                  {summary.source === "preview"
                    ? "Refund summary:"
                    : "Payment summary:"}
                </div>
                {previewLoading && !preview ? (
                  <div
                    style={{
                      fontSize: "14px",
                      lineHeight: "28px",
                      color: "#777",
                    }}
                  >
                    Calculating refundable amount…
                  </div>
                ) : (
                  <ul
                    style={{
                      fontSize: "16px",
                      lineHeight: "28px",
                      fontWeight: "500",
                    }}
                  >
                    {summary.wallet > 0 && (
                      <li>
                        {`${formatCurrency(summary.wallet)} `}
                        to Zyla wallet
                      </li>
                    )}

                    {summary.external > 0 && (
                      <li>
                        {`${formatCurrency(summary.external)} `}
                        to the original payment method
                      </li>
                    )}

                    {summary.total <= 0 && <li>Nothing left to refund</li>}

                    <li
                      style={{
                        fontWeight: "600",
                        fontSize: "16px",
                        marginTop: "8px",
                      }}
                    >
                      Total: {`${formatCurrency(summary.total)}`}
                    </li>
                  </ul>
                )}

                <Row>
                  {isOnlyRefund && (
                    <Col xs={12} sm={12} md={12} lg={12}>
                      <div className="navigo-module-create-service-field-container">
                        <div className="navigo-module-label-darker">{`Remark related to refund`}</div>
                        <div>
                          <TextField
                            value={reason}
                            lineDirection="left"
                            placeholder="Type here"
                            onChange={(e) => {
                              const value = e;
                              if (value.length <= 250) {
                                setReason(value);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  )}

                  <div
                    style={{
                      marginTop: "35px",
                      marginBottom: "15px",
                      textAlign: "center",
                      width: "100%",
                      fontWeight: "500",
                    }}
                  >
                    {forceRefund ? (
                      <>
                        <div>Still want to continue with the refund?</div>
                        <div>
                          Just type “REFUND” in the box below, then tap
                          “Initiate Refund”
                        </div>
                      </>
                    ) : (
                      <div>
                        To initiate the refund, type “REFUND” in the box below,
                        then tap “Initiate Refund”
                      </div>
                    )}
                  </div>
                </Row>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "1px solid #d3d3d3",
                    borderRadius: "6px",
                    padding: "4px 16px",
                    // 126px field + the 16px padding either side, so the input
                    // itself is 126px wide and the box stays centred.
                    width: "158px",
                    margin: "0 auto",
                  }}
                >
                  <TextField
                    id="refund-code"
                    lineDirection="left"
                    style={{
                      width: "126px",
                    }}
                    value={typed}
                    placeholder="Type here"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="characters"
                    spellCheck={false}
                    onChange={(value) => {
                      let filtered = value.replace(/[^a-zA-Z]/g, "");
                      filtered = filtered.toUpperCase().slice(0, 6);
                      setTyped(filtered);
                    }}
                  />
                </div>

                {isRetryable && (
                  <div
                    style={{
                      marginTop: "20px",
                      padding: "10px 14px",
                      borderRadius: "6px",
                      backgroundColor: "#FDECEA",
                      color: "#B3261E",
                      fontSize: "14px",
                      lineHeight: "22px",
                    }}
                  >
                    <div style={{ fontWeight: "600" }}>
                      {isFailed
                        ? "Refund failed — nothing was refunded"
                        : "Refund partially completed"}
                    </div>
                    <div>
                      {isFailed
                        ? `Failed on the first order (${
                            refundResult?.failedOrderId || "-"
                          }): ${refundResult?.error || "unknown error"}`
                        : `Refunded ${formatCurrency(
                            refundResult?.totalRefunded?.total,
                          )} across ${refundResult?.refundIds?.length || 0} order(s), then failed on order ${
                            refundResult?.failedOrderId || "-"
                          }: ${refundResult?.error || "unknown error"}`}
                    </div>
                    <div>
                      Retrying is safe — only the balance that is still
                      refundable will be paid out.
                    </div>
                  </div>
                )}

                <div style={{ marginTop: "25px", textAlign: "center" }}>
                  <Button
                    raised
                    disabled={typed !== "REFUND"}
                    style={
                      typed === "REFUND"
                        ? getButtonStyle("zyOrange")
                        : getButtonStyle("zyDisabled")
                    }
                    onClick={() => {
                      if (isOnlyRefund) {
                        handleOnlyRefund(appointmentObj);
                      } else {
                        handleCancelAppointment(
                          statusToBeApplied,
                          appointmentObj,
                        );
                      }
                    }}
                  >
                    {isRetryable ? "Retry refund" : "Initiate refund"}
                  </Button>
                  <Button
                    raised
                    style={getButtonStyle("zyGrey")}
                    onClick={() => {
                      setTyped("");
                      setReason("");
                      handleClose();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}

            {state === 2 && (
              <div style={{ textAlign: "center", padding: "40px 10px" }}>
                <img
                  src={successGif}
                  alt="success"
                  style={{ width: "180px" }}
                />
                {isOnlyRefund ||
                newRefundStatus === REFUND_STATUS.ELIGIBLE_APPLICABLE ? (
                  <>
                    <h4>Refund Initiated successfully</h4>
                    {refundResult?.groupId && (
                      <div
                        style={{
                          marginTop: "10px",
                          fontWeight: "500",
                          fontSize: "15px",
                        }}
                      >
                        {`Refund ID: ${refundResult.groupId}`}
                        {refundResult?.refundIds?.length > 1 &&
                          ` (${refundResult.refundIds.length} orders)`}
                      </div>
                    )}
                    <div
                      style={{
                        marginTop: "15px",
                        textAlign: "center",
                        width: "100%",
                        fontWeight: "400",
                      }}
                    >
                      Refund will be processed to the original payment sources
                      within 7 working days.
                    </div>
                  </>
                ) : (
                  <>
                    <h4>Appointment cancelled successfully</h4>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContainer>
    </div>
  );
}
